import React, { useState } from 'react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Card, DatePicker, Select, Row, Col, Statistic, Divider } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';

const { RangePicker } = DatePicker;
const { Option } = Select;

// Datos de ejemplo
const salesData = [
  { name: 'Ene', ventas: 4000, clientes: 2400, devoluciones: 240 },
  { name: 'Feb', ventas: 3000, clientes: 1398, devoluciones: 221 },
  { name: 'Mar', ventas: 2000, clientes: 9800, devoluciones: 229 },
  { name: 'Abr', ventas: 2780, clientes: 3908, devoluciones: 200 },
  { name: 'May', ventas: 1890, clientes: 4800, devoluciones: 218 },
  { name: 'Jun', ventas: 2390, clientes: 3800, devoluciones: 250 },
  { name: 'Jul', ventas: 3490, clientes: 4300, devoluciones: 210 },
];

const categoryData = [
  { name: 'Electrónicos', value: 400 },
  { name: 'Ropa', value: 300 },
  { name: 'Alimentos', value: 300 },
  { name: 'Hogar', value: 200 },
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const regionData = [
  { name: 'Norte', ventas: 4000, meta: 2400 },
  { name: 'Sur', ventas: 3000, meta: 1398 },
  { name: 'Este', ventas: 2000, meta: 9800 },
  { name: 'Oeste', ventas: 2780, meta: 3908 },
];

const Analytics = () => {

  const [dateRange, setDateRange] = useState([]);
  const [category, setCategory] = useState('todos');
  const [region, setRegion] = useState('todas');

  const filteredData = salesData.filter(item => {
    // Aquí iría la lógica de filtrado real basada en los filtros
    return true;
  });

  return (
    <div>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">Dashboard de Ventas</h1>

        {/* Filtros */}
        <Card className="mb-6 shadow-md">
          <Row gutter={16}>
            <Col span={8}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Rango de fechas</label>
                <RangePicker
                  className="w-full" onChange={(value: any) => setDateRange(value)}
                />
              </div>
            </Col>
            <Col span={8}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
                <Select
                  className="w-full"
                  defaultValue="todos"
                  onChange={setCategory}
                >
                  <Option value="todos">Todas las categorías</Option>
                  <Option value="electronicos">Electrónicos</Option>
                  <Option value="ropa">Ropa</Option>
                  <Option value="alimentos">Alimentos</Option>
                </Select>
              </div>
            </Col>
            <Col span={8}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Región</label>
                <Select
                  className="w-full"
                  defaultValue="todas"
                  onChange={setRegion}
                >
                  <Option value="todas">Todas las regiones</Option>
                  <Option value="norte">Norte</Option>
                  <Option value="sur">Sur</Option>
                  <Option value="este">Este</Option>
                  <Option value="oeste">Oeste</Option>
                </Select>
              </div>
            </Col>
          </Row>
        </Card>

        {/* KPI Cards */}
        <Row gutter={16} className="mb-6">
          <Col span={6}>
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <Statistic
                title="Ventas Totales"
                value={112893}
                precision={2}
                valueStyle={{ color: '#3f8600' }}
                prefix={<ArrowUpOutlined />}
                suffix="$"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <Statistic
                title="Clientes Nuevos"
                value={1245}
                valueStyle={{ color: '#3f8600' }}
                prefix={<ArrowUpOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <Statistic
                title="Devoluciones"
                value={89}
                valueStyle={{ color: '#cf1322' }}
                prefix={<ArrowDownOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <Statistic
                title="Tasa de Conversión"
                value={3.24}
                precision={2}
                valueStyle={{ color: '#3f8600' }}
                prefix={<ArrowUpOutlined />}
                suffix="%"
              />
            </Card>
          </Col>
        </Row>

        {/* Gráficos principales */}
        <Row gutter={16} className="mb-6">
          <Col span={16}>
            <Card title="Tendencia de Ventas" className="shadow-md h-full">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={filteredData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="colorVentas" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                    <Tooltip />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="ventas"
                      stroke="#8884d8"
                      fillOpacity={1}
                      fill="url(#colorVentas)"
                    />
                    <Area
                      type="monotone"
                      dataKey="clientes"
                      stroke="#82ca9d"
                      fillOpacity={1}
                      fill="url(#colorClientes)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Col>
          <Col span={8}>
            <Card title="Ventas por Categoría" className="shadow-md h-full">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={categoryData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {categoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Gráficos secundarios */}
        <Row gutter={16}>
          <Col span={12}>
            <Card title="Ventas vs Meta por Región" className="shadow-md">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={regionData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="ventas" fill="#8884d8" name="Ventas Reales" />
                    <Bar dataKey="meta" fill="#82ca9d" name="Meta" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Col>
          <Col span={12}>
            <Card title="Devoluciones Mensuales" className="shadow-md">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={filteredData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="devoluciones"
                      stroke="#ff7300"
                      strokeWidth={2}
                      activeDot={{ r: 8 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Col>
        </Row>

        <Divider />
      </div>
    </div>
  )
}

export default Analytics